import { useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import {
  RefreshCw,
  Sparkles,
  Package,
  Activity as ActivityIcon,
  TrendingUp,
  Filter as FilterIcon,
  HelpCircle,
  Download,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { PageHeader } from '../components/atlas/PageHeader';
import { LoadingSpinner } from '../components/common/LoadingSpinner';
import { ProductSelector } from '../components/common/ProductSelector';
import { useProductKpis } from '../hooks/useProductKpis';
import type { KpiCategory, KpiValue } from '../services/product-kpis.service';
import { useAccessibleProducts } from '../hooks/useAccessibleProducts';

const CATEGORY_ORDER: KpiCategory[] = [
  'Aquisição',
  'Produto',
  'Engajamento',
  'Conversão Potencial',
  'Qualidade do Tráfego',
];

const CATEGORY_META: Record<KpiCategory, { icon: typeof Sparkles; description: string }> = {
  Aquisição: {
    icon: Sparkles,
    description: 'Volume e qualidade dos signups vindos das campanhas',
  },
  Produto: {
    icon: Package,
    description: 'Uso real do core (PRDs, projetos, specs)',
  },
  Engajamento: {
    icon: ActivityIcon,
    description: 'Retenção, sessões e usuários recorrentes',
  },
  'Conversão Potencial': {
    icon: TrendingUp,
    description: 'Sinais fortes de intenção de upgrade e leads quentes',
  },
  'Qualidade do Tráfego': {
    icon: FilterIcon,
    description: 'Bounce, fricção e profundidade de interação',
  },
};

function formatGeneratedAt(iso: string) {
  try {
    const d = new Date(iso);
    return d.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

function KpiCard({ kpi }: { kpi: KpiValue }) {
  const isUnavailable = kpi.status === 'UNAVAILABLE';

  return (
    <div
      className={`p-4 rounded-lg border ${
        isUnavailable ? 'border-dashed border-border bg-canvas/50' : 'border-border bg-surface'
      } flex flex-col gap-2 min-h-[150px]`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="text-[11px] uppercase tracking-wider text-ink-tertiary font-medium">
          {kpi.label}
        </div>
        {isUnavailable && (
          <span
            title={kpi.unavailableReason ?? 'Dados ainda não disponíveis'}
            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-subtle text-ink-tertiary"
          >
            <HelpCircle className="w-3 h-3" />
            Em breve
          </span>
        )}
      </div>

      <div
        className={`text-2xl font-semibold leading-tight ${
          isUnavailable ? 'text-ink-tertiary' : 'text-ink-primary'
        }`}
      >
        {kpi.display}
      </div>

      <p className="text-xs text-ink-secondary leading-snug">{kpi.definition}</p>
      <p className="text-[11px] text-ink-tertiary italic leading-snug mt-auto">{kpi.usage}</p>
    </div>
  );
}

function CategorySection({ category, kpis }: { category: KpiCategory; kpis: KpiValue[] }) {
  const meta = CATEGORY_META[category];
  const Icon = meta.icon;
  const computed = kpis.filter((k) => k.status === 'COMPUTED').length;

  return (
    <section className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-md bg-subtle flex items-center justify-center text-ink-primary">
            <Icon className="w-4 h-4" />
          </div>
          <div>
            <h2 className="font-serif text-xl text-ink-primary font-semibold">{category}</h2>
            <p className="text-xs text-ink-tertiary">{meta.description}</p>
          </div>
        </div>
        <span className="text-[11px] text-ink-tertiary">
          {computed}/{kpis.length} disponíveis
        </span>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {kpis.map((kpi) => (
          <KpiCard key={kpi.key} kpi={kpi} />
        ))}
      </div>
    </section>
  );
}

function toIsoOrUndef(value: string, end = false): string | undefined {
  if (!value) {
    return undefined;
  }
  // value is YYYY-MM-DD; convert to start/end-of-day ISO
  const [y, m, d] = value.split('-').map(Number);
  if (!y || !m || !d) {
    return undefined;
  }
  const date = end
    ? new Date(Date.UTC(y, m - 1, d, 23, 59, 59, 999))
    : new Date(Date.UTC(y, m - 1, d, 0, 0, 0, 0));
  return date.toISOString();
}

export default function ProductKpisPage() {
  const { data: products = [] } = useAccessibleProducts();
  const [searchParams, setSearchParams] = useSearchParams();

  const urlProductId = searchParams.get('productId');
  const defaultProductId = products[0]?.id ?? null;
  const productId = urlProductId ? Number(urlProductId) : defaultProductId;

  useEffect(() => {
    if (!urlProductId && defaultProductId) {
      const next = new URLSearchParams(searchParams);
      next.set('productId', String(defaultProductId));
      setSearchParams(next, { replace: true });
    }
  }, [urlProductId, defaultProductId, searchParams, setSearchParams]);

  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [isExporting, setIsExporting] = useState(false);
  const reportRef = useRef<HTMLDivElement>(null);

  const handleProductChange = (id: number) => {
    const next = new URLSearchParams(searchParams);
    next.set('productId', String(id));
    setSearchParams(next, { replace: false });
  };

  const params = useMemo(
    () => ({
      productId: productId ?? undefined,
      startDate: toIsoOrUndef(startDate),
      endDate: toIsoOrUndef(endDate, true),
    }),
    [productId, startDate, endDate]
  );

  const { data, isLoading, isError, refetch, isFetching } = useProductKpis(params);
  const queryClient = useQueryClient();

  const product = products.find((p) => p.id === productId) ?? null;
  const selectedProductName = product?.name ?? 'Selecione um produto';

  const grouped = useMemo(() => {
    if (!data) {
      return null;
    }
    const map = new Map<KpiCategory, KpiValue[]>();
    for (const cat of CATEGORY_ORDER) {
      map.set(cat, []);
    }
    for (const kpi of data.kpis) {
      map.get(kpi.category)?.push(kpi);
    }
    return map;
  }, [data]);

  const handleRefresh = () => {
    queryClient.invalidateQueries({ queryKey: ['product-kpis'] });
    refetch();
  };

  const handleClearDates = () => {
    setStartDate('');
    setEndDate('');
  };

  const handleExportPdf = async () => {
    if (!reportRef.current || !data) {
      return;
    }
    setIsExporting(true);
    try {
      const [{ default: html2canvas }, { jsPDF }] = await Promise.all([
        import('html2canvas'),
        import('jspdf'),
      ]);

      const canvas = await html2canvas(reportRef.current, {
        scale: 2,
        backgroundColor: '#ffffff',
        useCORS: true,
        logging: false,
      });

      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });

      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = pageWidth - 20;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      let position = 10;
      let heightLeft = imgHeight;

      pdf.addImage(imgData, 'PNG', 10, position, imgWidth, imgHeight);
      heightLeft -= pageHeight - 20;

      while (heightLeft > 0) {
        position = heightLeft - imgHeight + 10;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 10, position, imgWidth, imgHeight);
        heightLeft -= pageHeight - 20;
      }

      const today = new Date().toISOString().slice(0, 10);
      const productSlug = selectedProductName.toLowerCase().replace(/[^a-z0-9]+/g, '-');
      pdf.save(`relatorio-kpis-${productSlug}-${today}.pdf`);
      toast.success('PDF gerado');
    } catch (err) {
      console.error('PDF export failed', err);
      toast.error('Não foi possível gerar o PDF');
    } finally {
      setIsExporting(false);
    }
  };

  const rangeLabel = (() => {
    if (!data) {
      return '';
    }
    const fmt = (iso: string | null) => {
      if (!iso) {
        return null;
      }
      try {
        return new Date(iso).toLocaleDateString('pt-BR');
      } catch {
        return null;
      }
    };
    const from = fmt(data.range.startDate);
    const to = fmt(data.range.endDate);
    if (from && to) {
      return ` · ${from} → ${to}`;
    }
    if (from) {
      return ` · desde ${from}`;
    }
    if (to) {
      return ` · até ${to}`;
    }
    return ' · todo o período';
  })();

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Relatórios"
        title="KPIs"
        subtitle={
          data
            ? `${selectedProductName} · ${data.validUsers.toLocaleString('pt-BR')} usuários válidos de ${data.totalUsers.toLocaleString('pt-BR')}${rangeLabel}`
            : 'Métricas de aquisição, produto, engajamento e conversão dos leads importados via integração'
        }
        actions={
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleRefresh}
              disabled={isFetching}
              className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium border border-border text-ink-secondary bg-surface rounded-md hover:bg-subtle transition-colors disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${isFetching ? 'animate-spin' : ''}`} />
              Atualizar
            </button>
            <button
              type="button"
              onClick={handleExportPdf}
              disabled={isExporting || !data}
              className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium border border-ink-primary text-ink-primary bg-surface rounded-md hover:bg-subtle transition-colors disabled:opacity-50"
            >
              <Download className={`w-4 h-4 ${isExporting ? 'animate-pulse' : ''}`} />
              {isExporting ? 'Gerando…' : 'Exportar PDF'}
            </button>
          </div>
        }
      />

      {/* Filters bar — responsive grid (stacks on mobile, columns on tablet+) */}
      <div className="p-4 rounded-lg border border-border bg-surface space-y-3">
        {products.length > 1 && (
          <div className="flex items-center gap-3">
            <ProductSelector value={productId} onChange={handleProductChange} />
          </div>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="flex flex-col gap-1 min-w-0">
            <label
              htmlFor="report-start"
              className="text-[11px] uppercase tracking-wider text-ink-tertiary font-medium"
            >
              Início
            </label>
            <input
              id="report-start"
              type="date"
              value={startDate}
              max={endDate || undefined}
              onChange={(e) => setStartDate(e.target.value)}
              className="input w-full max-w-full"
              style={{ minWidth: 0 }}
            />
          </div>
          <div className="flex flex-col gap-1 min-w-0">
            <label
              htmlFor="report-end"
              className="text-[11px] uppercase tracking-wider text-ink-tertiary font-medium"
            >
              Fim
            </label>
            <input
              id="report-end"
              type="date"
              value={endDate}
              min={startDate || undefined}
              onChange={(e) => setEndDate(e.target.value)}
              className="input w-full max-w-full"
              style={{ minWidth: 0 }}
            />
          </div>
        </div>
        <div className="flex items-center justify-between flex-wrap gap-2">
          <p className="text-[11px] text-ink-tertiary">
            Filtro aplicado sobre a data de cadastro (signup) dos usuários no produto.
          </p>
          {(startDate || endDate) && (
            <button
              type="button"
              onClick={handleClearDates}
              className="px-3 py-1.5 text-xs font-medium border border-border text-ink-secondary bg-surface rounded-md hover:bg-subtle transition-colors"
            >
              Limpar período
            </button>
          )}
        </div>
      </div>

      {isLoading && (
        <div className="flex items-center justify-center py-16">
          <LoadingSpinner size="lg" />
        </div>
      )}

      {isError && (
        <div className="p-4 rounded-md border border-danger/40 bg-danger/5 text-sm text-danger">
          Não foi possível carregar o relatório. Tente novamente em alguns segundos.
        </div>
      )}

      {data && grouped && (
        <div ref={reportRef} className="space-y-6 bg-canvas p-2">
          {/* PDF-friendly header (visible only when not loading) */}
          <div className="text-[11px] text-ink-tertiary border-b border-border pb-2">
            Gerado em {formatGeneratedAt(data.generatedAt)} · {selectedProductName}
            {rangeLabel}
          </div>

          {/* Resumo executivo: 3 KPIs primários */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {(['activation_rate', 'total_prds', 'reached_free_limit'] as const).map((key) => {
              const kpi = data.kpis.find((k) => k.key === key);
              if (!kpi) {
                return null;
              }
              return (
                <div
                  key={kpi.key}
                  className="p-4 rounded-lg border border-border bg-gradient-to-br from-subtle to-surface"
                >
                  <div className="text-[11px] uppercase tracking-wider text-ink-tertiary font-medium">
                    {kpi.label}
                  </div>
                  <div className="text-3xl font-semibold text-ink-primary mt-1">{kpi.display}</div>
                  <p className="text-xs text-ink-secondary mt-1">{kpi.usage}</p>
                </div>
              );
            })}
          </div>

          <div className="space-y-8">
            {CATEGORY_ORDER.map((cat) => {
              const kpis = grouped.get(cat) ?? [];
              if (kpis.length === 0) {
                return null;
              }
              return <CategorySection key={cat} category={cat} kpis={kpis} />;
            })}
          </div>

          <footer className="text-[11px] text-ink-tertiary pt-4 border-t border-border">
            Fonte: leads importados via integração externa (Docugen v1.41+), escopado por produto e
            visibilidade do usuário. KPIs marcados como &ldquo;Em breve&rdquo; dependem de leads já
            ressincronizados após a expansão de enrichment (specs, projetos, sessões e retornos
            D1/D3).
          </footer>
        </div>
      )}
    </div>
  );
}
